import type { Locale } from '../i18n/types';
import { isLocale, DEFAULT_LOCALE } from '../i18n/types';
import {
  emptyCustomizations,
  type ItemCustomizations,
} from './customizations';

export const STORE_KEY = 'yorisoi-users-v2';
const LEGACY_PROFILE_KEY = 'yorisoi-profile-v1';
const LEGACY_CHECKLIST_KEY = 'yorisoi-note-v1';
const LEGACY_LANG_KEY = 'yorisoi-lang';

export interface UserAccount {
  id: string;
  name: string;
  locale: Locale;
  dueDate: string | null;
  birthDate: string | null;
  onboarded: boolean;
  checked: string[];
  customizations: ItemCustomizations;
  updatedAt: string;
}

export interface UserStore {
  version: 2;
  activeId: string | null;
  accounts: UserAccount[];
}

function nowIso(): string {
  return new Date().toISOString();
}

export function createAccountId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `u_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function emptyAccount(partial?: Partial<UserAccount>): UserAccount {
  return {
    id: partial?.id ?? createAccountId(),
    name: partial?.name?.trim() || 'わたし',
    locale: partial?.locale && isLocale(partial.locale) ? partial.locale : DEFAULT_LOCALE,
    dueDate: partial?.dueDate ?? null,
    birthDate: partial?.birthDate ?? null,
    onboarded: Boolean(partial?.onboarded),
    checked: Array.isArray(partial?.checked) ? partial!.checked.filter((k) => typeof k === 'string') : [],
    customizations: emptyCustomizations(partial?.customizations),
    updatedAt: partial?.updatedAt ?? nowIso(),
  };
}

function loadLegacyMigration(): UserStore | null {
  if (typeof window === 'undefined') return null;
  try {
    const profileRaw = localStorage.getItem(LEGACY_PROFILE_KEY);
    const checklistRaw = localStorage.getItem(LEGACY_CHECKLIST_KEY);
    const langRaw = localStorage.getItem(LEGACY_LANG_KEY);
    if (!profileRaw && !checklistRaw) return null;

    let dueDate: string | null = null;
    let birthDate: string | null = null;
    let onboarded = false;
    if (profileRaw) {
      const p = JSON.parse(profileRaw) as {
        dueDate?: string | null;
        birthDate?: string | null;
        onboarded?: boolean;
      };
      dueDate = typeof p.dueDate === 'string' ? p.dueDate : null;
      birthDate = typeof p.birthDate === 'string' ? p.birthDate : null;
      onboarded = Boolean(p.onboarded) || Boolean(dueDate || birthDate);
    }

    let checked: string[] = [];
    if (checklistRaw) {
      const c = JSON.parse(checklistRaw) as { checked?: string[] };
      checked = Array.isArray(c.checked) ? c.checked : [];
    }

    const locale = isLocale(langRaw) ? langRaw : DEFAULT_LOCALE;
    const account = emptyAccount({
      name: 'わたし',
      locale,
      dueDate,
      birthDate,
      onboarded,
      checked,
    });
    return { version: 2, activeId: account.id, accounts: [account] };
  } catch {
    return null;
  }
}

export function loadStore(): UserStore {
  if (typeof window === 'undefined') {
    return { version: 2, activeId: null, accounts: [] };
  }
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<UserStore>;
      const accounts = Array.isArray(parsed.accounts)
        ? parsed.accounts.map((a) => emptyAccount(a))
        : [];
      const activeId =
        typeof parsed.activeId === 'string' && accounts.some((a) => a.id === parsed.activeId)
          ? parsed.activeId
          : accounts[0]?.id ?? null;
      return { version: 2, activeId, accounts };
    }
  } catch {
    // fall through to migration
  }

  const migrated = loadLegacyMigration();
  if (migrated) {
    saveStore(migrated);
    return migrated;
  }
  return { version: 2, activeId: null, accounts: [] };
}

export function saveStore(store: UserStore): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(store));
    // Keep language key in sync for the active account
    const active = store.accounts.find((a) => a.id === store.activeId);
    if (active) {
      localStorage.setItem(LEGACY_LANG_KEY, active.locale);
    }
  } catch {
    // ignore quota / private mode
  }
}

export function getActiveAccount(store: UserStore): UserAccount | null {
  if (!store.activeId) return null;
  return store.accounts.find((a) => a.id === store.activeId) ?? null;
}

export function upsertAccount(store: UserStore, account: UserAccount): UserStore {
  const next = emptyAccount({ ...account, updatedAt: nowIso() });
  const idx = store.accounts.findIndex((a) => a.id === next.id);
  const accounts = [...store.accounts];
  if (idx >= 0) accounts[idx] = next;
  else accounts.push(next);
  return {
    version: 2,
    activeId: next.id,
    accounts,
  };
}

export function removeAccount(store: UserStore, accountId: string): UserStore {
  const accounts = store.accounts.filter((a) => a.id !== accountId);
  const activeId =
    store.activeId === accountId ? accounts[0]?.id ?? null : store.activeId;
  return { version: 2, activeId, accounts };
}

export type BackupPayload = {
  version: 2;
  savedAt: string;
  account: UserAccount;
};

export function buildBackupPayload(account: UserAccount): BackupPayload {
  return {
    version: 2,
    savedAt: nowIso(),
    account: emptyAccount(account),
  };
}
